import { Point, Range, Editor, Transforms } from 'domains/reporter/RichTextEditor/core';
import { useCallback, useMemo, useRef, useEffect } from 'react';
import analytics from 'modules/analytics';
import { reporter } from 'modules/analytics/constants';

import { useRecoilState, useRecoilValue, useRecoilCallback } from 'recoil';
import { useFeatureFlagEnabled, FF } from 'modules/feature-flags';
import { isFocusModeState, isFocusModeSettingEnabledState } from 'domains/reporter/Reporter/state';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { logger } from 'modules/logger';
import {
  getHeadingSectionEdgesByNode,
  getHeadingNodeWithTextInRange,
} from 'domains/reporter/RichTextEditor/plugins/heading/utils/normalization';
import { getHeadingSectionEdgesByRegex } from 'domains/reporter/RichTextEditor/plugins/heading/utils';
import { HeadingLevel } from 'domains/reporter/RichTextEditor/plugins/heading/constants';
import { focusModeSessionMeasure } from 'domains/reporter/RichTextEditor/analytics/performance';

import {
  getHasMultipleInlineBookmarksInRange,
  getLastInlineBookmarkInRange,
  getLastInlineBookmarkInRangeIfEmpty,
  insertInlineBookmark,
} from 'domains/reporter/RichTextEditor/plugins/inlineBookmark/utils';
import {
  createParagraph,
  getParagraphNodeStartingWithTextInRange,
  findSearchTermInParagraphs,
  deleteTextRanges,
} from '../domains/reporter/RichTextEditor/plugins/paragraph/utils';
import { WithApolloContext } from 'modules/Apollo/types';

export enum FocusModeToggleMethod {
  Dictaphone = 'dictaphone',
  FormControl = 'form_control',
  MouseClick = 'mouse_click',
  VoiceCommand = 'voice_command',
}

type UseFocusModeReturn = {
  isFocusModeSettingEnabled: boolean;
  isFocusModeSettingVisible: boolean;
  isFocusModeOn: boolean;
  setIsFocusModeSettingEnabled: (isFocusModeSettingEnabled: boolean) => void;
  toggleFocusMode: (args: { method: FocusModeToggleMethod; enabled?: boolean }) => void;
  handleFocusModeMouseClick: () => void;
  getInsertionPoint: (editor: Editor, sections: Array<string>) => Nullable<Point>;
  getInsertionRange: (editor: Editor, sections: Array<string>) => Nullable<Range>;
  deleteTextInSection: (editor: Editor, text: string, section: Array<string>) => void;
  mostRecentFocusModeOperationRef: {
    current: Nullable<FocusModeOperationValue>;
  };
};

export const FocusModeOperation = Object.freeze({
  Delete: 'delete',
  Append: 'append',
});

export type FocusModeOperationType = keyof typeof FocusModeOperation;
export type FocusModeOperationValue = (typeof FocusModeOperation)[FocusModeOperationType];

export const useFocusMode = ({ context }: WithApolloContext): UseFocusModeReturn => {
  const isFocusModeOn = useRecoilValue(isFocusModeState);
  const [isFocusModeSettingEnabled, setIsFocusModeSettingEnabled] = useRecoilState(
    isFocusModeSettingEnabledState
  );
  const [isFocusModeFeatureEnabled] = useFeatureFlagEnabled(FF.REPORTER_FOCUS_MODE);
  const { data } = useCurrentUser({ context });
  const me = data?.me;
  const reporterSettings = me?.reporterSettings;
  const mostRecentFocusModeOperationRef = useRef<Nullable<FocusModeOperationValue>>(null);

  // Visibility of Focus Mode in the Reporter setting
  const isFocusModeSettingVisible = isFocusModeFeatureEnabled;

  useEffect(() => {
    setIsFocusModeSettingEnabled(reporterSettings?.focusMode?.enabled ?? false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleFocusMode = useRecoilCallback(
    ({ set, snapshot }) =>
      ({ method, enabled }: { method: FocusModeToggleMethod; enabled?: boolean }) => {
        const currentFocusMode = snapshot.getLoadable(isFocusModeState).getValue();
        const newEnabled = enabled ?? !currentFocusMode;

        if (newEnabled === currentFocusMode) {
          return;
        }

        if (method === FocusModeToggleMethod.MouseClick && !newEnabled) {
          logger.info('[useFocusMode] Mouse click detected, exiting focus mode');
        }

        analytics.track(reporter.usr.focusModeToggled, { enabled: newEnabled, method });

        // The mark is window-global, so the session spans every useFocusMode instance.
        if (newEnabled) {
          focusModeSessionMeasure.start();
        } else {
          focusModeSessionMeasure.measure();
        }

        set(isFocusModeState, newEnabled);
      },
    []
  );

  // Handle mouse interactions inside the report to exit focus mode.
  const handleFocusModeMouseClick = useCallback(() => {
    toggleFocusMode({ method: FocusModeToggleMethod.MouseClick, enabled: false });
  }, [toggleFocusMode]);

  const getInlineBookmarkPointForInsertion = useCallback(
    (editor: Editor, range: Range): Nullable<Point> => {
      const hasMultipleInlineBookmarksInRange = getHasMultipleInlineBookmarksInRange(editor, range);
      const lastInlineBookmarkInRange = getLastInlineBookmarkInRange(editor, range);
      const lastEmptyInlineBookmarkInRange = getLastInlineBookmarkInRangeIfEmpty(editor, range);

      if (lastInlineBookmarkInRange != null && !hasMultipleInlineBookmarksInRange) {
        return Editor.end(editor, lastInlineBookmarkInRange[1]);
      } else if (lastEmptyInlineBookmarkInRange != null && hasMultipleInlineBookmarksInRange) {
        return Editor.end(editor, lastEmptyInlineBookmarkInRange[1]);
      }

      // if no inline bookmarks found
      Transforms.insertNodes(editor, [createParagraph()], {
        at: Editor.end(editor, range),
        select: true,
      });
      insertInlineBookmark(editor, {
        select: true,
        location: Editor.range(editor, Editor.end(editor, range)),
      });

      const { selection } = editor;
      if (selection == null) {
        return Editor.end(editor, range);
      }

      return Editor.end(editor, selection);
    },
    []
  );

  const getInsertionRange = useCallback(
    (editor: Editor, sections: Array<string>): Nullable<Range> => {
      if (sections.length === 0) {
        return null;
      }

      const headingNodeText = sections[0];
      const headingNodeEntry = getHeadingNodeWithTextInRange({
        editor,
        text: headingNodeText,
        level: HeadingLevel.H1,
      });

      if (headingNodeEntry != null) {
        const headingSectionEdges = getHeadingSectionEdgesByNode(
          editor,
          headingNodeEntry,
          HeadingLevel.H1
        );

        if (headingSectionEdges == null) {
          return null;
        }

        // If there is only one section, then we've already found the range
        if (sections.length === 1) {
          return headingSectionEdges;
        }

        const subheadingNodeEntry = getHeadingNodeWithTextInRange({
          editor,
          text: sections[1],
          level: HeadingLevel.H2,
          range: headingSectionEdges,
        });

        let insertionSectionEdges = null;

        if (subheadingNodeEntry != null) {
          insertionSectionEdges = getHeadingSectionEdgesByNode(
            editor,
            subheadingNodeEntry,
            HeadingLevel.H2
          );
        } else {
          const paragraphEntry = getParagraphNodeStartingWithTextInRange({
            editor,
            text: sections[1] + ':',
            range: headingSectionEdges,
          });
          if (paragraphEntry != null) {
            // When subsection headings are paragraph-style
            // scope to the subsection body, not only the heading paragraph line.
            insertionSectionEdges =
              getHeadingSectionEdgesByRegex(editor, sections) ??
              (() => {
                const edges = Editor.edges(editor, paragraphEntry[1]);
                return { anchor: edges[0], focus: edges[1] };
              })();
          }
        }

        if (insertionSectionEdges == null) {
          return headingSectionEdges;
        } else {
          return insertionSectionEdges;
        }
      } else {
        const headingSectionEdges = getHeadingSectionEdgesByRegex(editor, sections);
        if (headingSectionEdges == null) {
          return null;
        }

        return headingSectionEdges;
      }
    },
    []
  );

  const getInsertionPoint = useCallback(
    (editor: Editor, sections: Array<string>): Nullable<Point> => {
      if (editor == null) {
        return null;
      }

      if (sections.length === 0) {
        return Editor.end(editor, []);
      }

      if (mostRecentFocusModeOperationRef.current === FocusModeOperation.Delete) {
        return editor.selection?.anchor;
      }

      const headingNodeText = sections[0];
      const headingNodeEntry = getHeadingNodeWithTextInRange({
        editor,
        text: headingNodeText,
        level: HeadingLevel.H1,
      });

      if (headingNodeEntry != null) {
        const headingSectionEdges = getHeadingSectionEdgesByNode(
          editor,
          headingNodeEntry,
          HeadingLevel.H1
        );

        if (headingSectionEdges == null) {
          return Editor.end(editor, []);
        }

        // If there is only one section, then we've already found the range
        if (sections.length === 1) {
          return getInlineBookmarkPointForInsertion(editor, headingSectionEdges);
        }

        const subheadingNodeEntry = getHeadingNodeWithTextInRange({
          editor,
          text: sections[1],
          level: HeadingLevel.H2,
          range: headingSectionEdges,
        });

        let insertionSectionEdges = null;

        if (subheadingNodeEntry != null) {
          insertionSectionEdges = getHeadingSectionEdgesByNode(
            editor,
            subheadingNodeEntry,
            HeadingLevel.H2
          );
        } else {
          const paragraphEntry = getParagraphNodeStartingWithTextInRange({
            editor,
            text: sections[1] + ':',
            range: headingSectionEdges,
          });
          if (paragraphEntry != null) {
            // When subsection headings are paragraph-style
            // scope to the subsection body, not only the heading paragraph line.
            insertionSectionEdges =
              getHeadingSectionEdgesByRegex(editor, sections) ??
              (() => {
                const edges = Editor.edges(editor, paragraphEntry[1]);
                return { anchor: edges[0], focus: edges[1] };
              })();
          }
        }

        if (insertionSectionEdges == null) {
          return Editor.end(editor, headingSectionEdges);
        } else {
          return getInlineBookmarkPointForInsertion(editor, insertionSectionEdges);
        }
      } else {
        const headingSectionEdges = getHeadingSectionEdgesByRegex(editor, sections);
        if (headingSectionEdges == null) {
          return Editor.end(editor, []);
        }

        return getInlineBookmarkPointForInsertion(editor, headingSectionEdges);
      }
    },
    [getInlineBookmarkPointForInsertion]
  );

  const deleteTextInSection = useCallback(
    (editor: Editor, text: string, sections: Array<string>): void => {
      if (editor == null || sections.length === 0) {
        return;
      }
      // Get the extent of the section so we can search for paragraph nodes
      // in which to delete the specified text.
      const headingSectionEdges = getInsertionRange(editor, sections);
      if (headingSectionEdges == null) {
        return;
      }

      const results = findSearchTermInParagraphs(editor, headingSectionEdges, text);
      const lastDeletedAt = deleteTextRanges(editor, results);
      logger.info('[deleteTextInSection] Deleted existing default text in focus mode', {
        results,
        lastDeletedAt,
      });
      if (lastDeletedAt) {
        mostRecentFocusModeOperationRef.current = FocusModeOperation.Delete;
      }
    },
    [getInsertionRange]
  );

  return useMemo(
    () => ({
      isFocusModeSettingVisible,
      isFocusModeSettingEnabled,
      isFocusModeOn,
      setIsFocusModeSettingEnabled,
      toggleFocusMode,
      handleFocusModeMouseClick,
      getInsertionPoint,
      getInsertionRange,
      deleteTextInSection,
      mostRecentFocusModeOperationRef,
    }),
    [
      isFocusModeSettingVisible,
      isFocusModeSettingEnabled,
      isFocusModeOn,
      setIsFocusModeSettingEnabled,
      toggleFocusMode,
      handleFocusModeMouseClick,
      getInsertionPoint,
      getInsertionRange,
      deleteTextInSection,
      mostRecentFocusModeOperationRef,
    ]
  );
};
